import { ChangeEvent, FC, FormEvent, useState } from "react";

interface LanguageFormProps {
  initialValues: { language: string; isActive: string };
  onSubmit: (data: { language: string; isActive: string }) => void;
  onCancel: () => void;
  mode: "add" | "edit" | "view";
  isSubmitting?: boolean;
}

export const LanguageForm: FC<LanguageFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  mode,
  isSubmitting: parentSubmitting = false,
}) => {
  const [formData, setFormData] = useState<{
    language: string;
    isActive: string;
  }>(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitting || parentSubmitting) return;

    try {
      setIsSubmitting(true);
      await onSubmit(formData);
    } catch (error) {
      console.error("Submit failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const anySubmitting = isSubmitting || parentSubmitting;

  return (
    <form onSubmit={handleSubmit}>
      <div className="font-body tracking-wider bg-white px-6 py-12 rounded-md shadow-md my-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Language Name *
            </label>
            <input
              type="text"
              name="language"
              value={formData.language}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md text-sm"
              required
              readOnly={mode === "view"}
              maxLength={100}
              placeholder="Enter language name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Status
            </label>
            <select
              name="isActive"
              value={formData.isActive}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md text-sm"
              required
              disabled={mode === "view"}
            >
              <option value="Y">Active</option>
              <option value="N">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {mode !== "view" && (
        <div className="flex items-center justify-end gap-3 mt-4">
          <button
            className="px-6 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white text-blue-600 hover:bg-gray-50 disabled:opacity-50"
            type="button"
            onClick={onCancel}
            disabled={anySubmitting}
          >
            Cancel
          </button>
          <button
            className={`px-6 py-2 text-white text-sm font-medium rounded-md flex items-center gap-2 ${
              anySubmitting ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
            }`}
            type="submit"
            disabled={anySubmitting}
          >
            {anySubmitting && (
              <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {anySubmitting ? "Processing..." : mode === "add" ? "Add Language" : "Save Changes"}
          </button>
        </div>
      )}
    </form>
  );
};